import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from './formatters';

export const generateReceiptPDF = (sale, userData) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [80, 297] // Formato de ticket (80mm x 297mm)
  });

  let yPosition = 10;
  const lineHeight = 5;
  const leftMargin = 5;
  const pageWidth = 80;
  const contentWidth = pageWidth - (leftMargin * 2);

  // Configurar fuente
  doc.setFontSize(12);
  
  // Encabezado
  doc.setFont(undefined, 'bold');
  doc.text(userData?.pharmacyName || 'NomoFarma', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight * 1.5;
  
  // Información del negocio
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  
  if (userData?.rfc) {
    doc.text(`RFC/NIT: ${userData.rfc}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += lineHeight;
  }
  
  if (userData?.businessAddress) {
    doc.text(userData.businessAddress, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += lineHeight;
  }
  
  if (userData?.businessCity) {
    doc.text(userData.businessCity, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += lineHeight;
  }
  
  if (userData?.businessPhone) {
    doc.text(`Tel: ${userData.businessPhone}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += lineHeight;
  }
  
  if (userData?.businessEmail) {
    doc.text(userData.businessEmail, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += lineHeight;
  }
  
  // Fecha y hora
  yPosition += lineHeight;
  const fechaVenta = sale.createdAt ? new Date(sale.createdAt) : new Date(sale.date);
  doc.text(format(fechaVenta, 'dd/MM/yyyy HH:mm', { locale: es }), pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight;
  
  // Cliente
  doc.text(`Cliente: ${sale.customerName || 'Público General'}`, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight;
  
  // Línea divisoria
  yPosition += lineHeight / 2;
  doc.line(leftMargin, yPosition, pageWidth - leftMargin, yPosition);
  yPosition += lineHeight;
  
  // Título productos
  doc.setFont(undefined, 'bold');
  doc.text('PRODUCTOS', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight * 1.5;
  
  // Productos
  doc.setFont(undefined, 'normal');
  sale.items.forEach((item, index) => {
    // Nombre del producto
    const productName = `${index + 1}. ${item.productName}`;
    const lines = doc.splitTextToSize(productName, contentWidth);
    lines.forEach(line => {
      doc.text(line, leftMargin, yPosition);
      yPosition += lineHeight;
    });
    
    // Cantidad y precio
    const cantidadPrecio = `${item.quantity} x ${formatCurrency(item.unitPrice)}`;
    const subtotal = formatCurrency(item.total);
    doc.text(cantidadPrecio, leftMargin + 5, yPosition);
    doc.text(subtotal, pageWidth - leftMargin, yPosition, { align: 'right' });
    yPosition += lineHeight * 1.5;
  });
  
  // Línea divisoria
  doc.line(leftMargin, yPosition, pageWidth - leftMargin, yPosition);
  yPosition += lineHeight;
  
  // Totales
  doc.text('Subtotal:', leftMargin, yPosition);
  doc.text(formatCurrency(sale.subtotal), pageWidth - leftMargin, yPosition, { align: 'right' });
  yPosition += lineHeight;
  
  if (sale.chargedWithTax !== false) {
    doc.text('IVA 16%:', leftMargin, yPosition);
    doc.text(formatCurrency(sale.tax), pageWidth - leftMargin, yPosition, { align: 'right' });
    yPosition += lineHeight;
  }
  
  // Total
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  yPosition += lineHeight / 2;
  doc.text('TOTAL:', leftMargin, yPosition);
  doc.text(formatCurrency(sale.total), pageWidth - leftMargin, yPosition, { align: 'right' });
  yPosition += lineHeight * 1.5;
  
  // Pie de página
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);
  doc.line(leftMargin, yPosition, pageWidth - leftMargin, yPosition);
  yPosition += lineHeight;
  
  doc.text('¡Gracias por su compra!', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight;
  doc.text('Conserve su ticket', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight;
  doc.text(`No. Ticket: ${sale.saleNumber || sale.id?.slice(-8)}`, pageWidth / 2, yPosition, { align: 'center' });
  
  return doc;
};

export const downloadReceiptPDF = (sale, userData) => {
  const doc = generateReceiptPDF(sale, userData);
  const fileName = `Recibo_${sale.saleNumber || sale.id?.slice(-8)}.pdf`;
  doc.save(fileName);
};

export const shareReceiptPDF = async (sale, userData) => {
  const doc = generateReceiptPDF(sale, userData);
  const fileName = `Recibo_${sale.saleNumber || sale.id?.slice(-8)}.pdf`;
  
  // Generar el PDF como blob
  const pdfBlob = doc.output('blob');
  
  // Crear un archivo
  const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
  
  // Verificar si el navegador soporta Web Share API
  if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        title: 'Recibo de Venta',
        text: `Recibo #${sale.saleNumber || sale.id?.slice(-8)}`,
        files: [file]
      });
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error compartiendo:', error);
        // Si falla el compartir, descargar el archivo
        downloadReceiptPDF(sale, userData);
      }
      return false;
    }
  } else {
    // Si no soporta compartir, descargar el archivo
    downloadReceiptPDF(sale, userData);
    return false;
  }
};